// src/components/layout/PageHeader.jsx
import React, { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';
import { HelpCircle, X, Info, Edit2, Save, ImagePlus, Trash2, Plus, Maximize2 } from 'lucide-react';
import planApi from '@api/planApi';

export function PageHeader({ 
  icon: Icon, 
  title, 
  description, 
  iconBgColor = "bg-blue-600", 
  auth, 
  helpId, 
  initialHelpText = "", 
}) {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // 🌟 (추가) 확대해서 볼 이미지 URL을 저장하는 상태
  const [selectedImage, setSelectedImage] = useState(null);
  
  const isAdmin = auth?.role === 'ROLE_ADMIN';

  // '사진 + 텍스트'를 하나의 세트(블록)로 관리하는 배열
  const [blocks, setBlocks] = useState([
    { id: Date.now(), text: initialHelpText, imageFile: null, previewUrl: null }
  ]);
  const [currentTokenId, setCurrentTokenId] = useState(null);

  // 도움말 모달이 열릴 때 DB에서 데이터 조회
  useEffect(() => {
    const fetchHelpData = async () => {
      if (isHelpOpen && helpId) {
        try {
          const res = await planApi.getHelp(helpId);
          const rawData = res.data !== undefined ? res.data : res;

          if (!rawData || rawData === "") {
            setBlocks([{ id: Date.now(), text: initialHelpText, previewUrl: null }]);
            return;
          }

          const savedBlocks = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
          
          if (Array.isArray(savedBlocks) && savedBlocks.length > 0) {
            setBlocks(savedBlocks);
            
            const existingToken = savedBlocks.find(b => b.previewUrl)?.previewUrl?.split('tokenId=')?.[1];
            if (existingToken) setCurrentTokenId(existingToken);
          } else {
            setBlocks([{ id: Date.now(), text: initialHelpText, previewUrl: null }]);
          }

        } catch (error) {
          console.error("도움말 로드 실패", error);
          setBlocks([{ id: Date.now(), text: initialHelpText, previewUrl: null }]);
        }
      }
    };
    fetchHelpData();
  }, [isHelpOpen, helpId, initialHelpText]);

  const handleAddBlock = () => {
    setBlocks(prev => [...prev, { id: Date.now(), text: '', imageFile: null, previewUrl: null }]);
  };

  const handleRemoveBlock = (idToRemove) => {
    setBlocks(prev => prev.filter(block => block.id !== idToRemove));
  };

  const handleTextChange = (id, newText) => {
    setBlocks(prev => prev.map(block => block.id === id ? { ...block, text: newText } : block));
  };

  const handleImageUpload = async (id, e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const res = await planApi.uploadHelpImage(file, currentTokenId);
      
      if (res.tokenId && !currentTokenId) {
        setCurrentTokenId(res.tokenId);
      }

      setBlocks(prev => prev.map(block => 
        block.id === id ? { ...block, previewUrl: res.imageUrl } : block
      ));
      
    } catch (error) {
      alert("이미지 업로드에 실패했습니다.");
    }
  };

  const handleRemoveImage = (id) => {
    setBlocks(prev => prev.map(block => 
      block.id === id ? { ...block, previewUrl: null } : block
    ));
  };

  const handleSave = async () => {
    try {
      const payload = {
        helpId: helpId,         
        tokenId: currentTokenId,
        blocks: blocks          
      };

      await planApi.saveHelp(payload);
      alert("도움말이 성공적으로 저장되었습니다.");
      setIsEditMode(false);
    } catch (error) {
      alert("도움말 저장에 실패했습니다.");
    }
  };

  const handleClose = () => {
    setIsEditMode(false);
    setIsHelpOpen(false);
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/uploaded_images/') && window.location.hostname === 'localhost') {
      return `http://localhost:8080${url}`;
    }
    return url;
  };

  return (
    <div className="flex items-start justify-between mb-6">
      {/* 타이틀 영역 */}
      <div className="flex items-start gap-3">
        <div className={clsx("flex h-10 w-10 items-center justify-center rounded-lg shadow-sm", iconBgColor)}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-800">{title}</h2>
          <p className="text-sm text-slate-500 mt-0.5">{description}</p>
        </div>
      </div>

      {/* 도움말 버튼 */}
      <button
        onClick={() => setIsHelpOpen(true)}
        className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 transition-all"
      >
        <HelpCircle className="h-5 w-5" />
        도움말
      </button>

      {/* 도움말 모달창 */}
      {isHelpOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden flex flex-col max-h-[85vh]">
            
            {/* 헤더 */}
            <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 bg-blue-50/50 shrink-0">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Info className="h-5 w-5 text-blue-600" />
                {title} 이용 안내
              </h3>
              <div className="flex items-center gap-2">
                {isAdmin && !isEditMode && (
                  <button onClick={() => setIsEditMode(true)} className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-200 transition-colors">
                    <Edit2 className="h-4 w-4" /> 수정
                  </button>
                )}
                {isAdmin && isEditMode && (
                  <button onClick={handleSave} className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 transition-colors">
                    <Save className="h-4 w-4" /> 저장
                  </button>
                )}
                <div className="h-4 w-px bg-slate-300 mx-1"></div>
                <button onClick={handleClose} className="rounded-lg p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors">
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* 본문 스크롤 영역 */}
            <div className="p-6 overflow-y-auto flex-1 bg-slate-50 relative">
              <div className="space-y-6 max-w-2xl mx-auto">
                
                {blocks.map((block, index) => (
                  <div key={block.id} className="relative rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                    
                    {/* 수정 모드일 때만 보이는 블록 제어(삭제) 영역 */}
                    {isEditMode && (
                      <div className="absolute right-3 top-3 flex items-center gap-2 z-10">
                        <span className="text-xs font-bold text-slate-400 mr-2">Step {index + 1}</span>
                        <button onClick={() => handleRemoveBlock(block.id)} className="text-slate-400 hover:text-red-500 transition-colors p-1" title="블록 삭제">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}

                    <div className="flex flex-col gap-4 mt-2">
                      {/* 이미지 영역 */}
                      {isEditMode ? (
                        <div className="mt-2">
                          {block.previewUrl ? (
                            <div className="relative inline-block border border-slate-200 rounded-lg overflow-hidden bg-slate-50 group">
                              <img src={getImageUrl(block.previewUrl)} alt="preview" className="max-h-60 object-contain" />
                              <button onClick={() => handleRemoveImage(block.id)} className="absolute top-2 right-2 bg-slate-900/60 text-white p-1.5 rounded-md hover:bg-red-500 transition-colors">
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <div>
                              <input 
                                type="file" 
                                accept="image/*" 
                                id={`file-${block.id}`} 
                                className="hidden" 
                                onChange={(e) => handleImageUpload(block.id, e)} 
                              />
                              <label htmlFor={`file-${block.id}`} className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 hover:bg-slate-100 hover:border-blue-400 cursor-pointer transition-colors">
                                <ImagePlus className="h-6 w-6 text-slate-400 mb-2" />
                                <span className="text-sm font-medium text-slate-600">클릭하여 사진 추가 (선택)</span>
                              </label>
                            </div>
                          )}
                        </div>
                      ) : (
                        // 🌟 (수정) 읽기 모드 이미지 표시 (클릭 이벤트 및 커서, 안내 아이콘 추가)
                        block.previewUrl && (
                          <div 
                            className="relative rounded-lg overflow-hidden border border-slate-100 bg-slate-50 flex justify-center group cursor-pointer hover:border-blue-300 transition-colors"
                            onClick={() => setSelectedImage(getImageUrl(block.previewUrl))}
                            title="클릭하여 확대 보기"
                          >
                            <img src={getImageUrl(block.previewUrl)} alt={`guide-step-${index + 1}`} className="max-h-80 object-contain transition-transform group-hover:scale-[1.02] duration-300" />
                            {/* 확대 아이콘 오버레이 */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors flex items-center justify-center">
                              <div className="bg-slate-900/70 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity transform scale-90 group-hover:scale-100 duration-200">
                                <Maximize2 className="h-5 w-5" />
                              </div>
                            </div>
                          </div>
                        )
                      )}

                      {/* 텍스트 영역 */}
                      {isEditMode ? (
                        <textarea
                          value={block.text}
                          onChange={(e) => handleTextChange(block.id, e.target.value)}
                          placeholder="이 단계에 대한 설명을 입력하세요..."
                          className="w-full h-28 rounded-lg border border-slate-300 p-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                        />
                      ) : (
                        // 읽기 모드 텍스트 표시
                        <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap flex items-start gap-3 mt-2">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600">
                            {index + 1}
                          </span>
                          <div className="pt-0.5">{block.text || "설명 없음"}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* 편집 모드일 때만 보이는 블록 추가 버튼 */}
                {isEditMode && (
                  <button 
                    onClick={handleAddBlock}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 py-4 text-sm font-semibold text-blue-600 hover:bg-blue-50 transition-colors shadow-sm"
                  >
                    <Plus className="h-5 w-5" /> 설명 블록 추가
                  </button>
                )}

                {/* 등록된 블록이 없을 때 (읽기 모드) */}
                {!isEditMode && blocks.length === 0 && (
                  <div className="text-center text-slate-400 py-10 bg-white rounded-xl border border-slate-200 shadow-sm">
                    등록된 도움말 내용이 없습니다.
                  </div>
                )}

              </div>
            </div>

            {/* 푸터 (읽기 모드일 때만 닫기 버튼 표시) */}
            {!isEditMode && (
              <div className="bg-white px-6 py-4 border-t border-slate-100 flex justify-end shrink-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.02)] z-10">
                <button onClick={handleClose} className="rounded-lg bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 transition-colors">
                  닫기
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 🌟 (추가) 이미지 크게 보기 (Lightbox) 모달 */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm animate-in fade-in duration-200 p-4 sm:p-10"
          onClick={() => setSelectedImage(null)} // 검은 배경 클릭 시 닫기
        >
          {/* 🌟 (핵심) max-w-5xl(최대 1024px)과 max-h-[80vh]를 주어 화면을 과도하게 덮지 않게 제한 */}
          <div className="relative w-full max-w-5xl flex items-center justify-center">
            {/* 확대된 이미지 */}
            <img 
              src={selectedImage} 
              alt="Enlarged view" 
              className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-2xl animate-in zoom-in-95 duration-200 bg-white"
              onClick={(e) => e.stopPropagation()} // 이미지 클릭 시 닫히지 않도록 방지
            />
            {/* 닫기 버튼 */}
            <button 
              onClick={() => setSelectedImage(null)} 
              className="absolute -top-4 -right-4 sm:-top-5 sm:-right-5 bg-white text-slate-900 p-2 rounded-full hover:bg-slate-200 shadow-xl transition-transform hover:scale-110 z-10"
              title="닫기"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}